/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';
import { Segment } from '@mairistem/composants';

import { ASSISTANT_STEPS } from '@/constants';
import {
  type Mention706702Data,
  Steps,
  type Acte,
  type ActeIndividu,
  type MentionAnts,
  type TypeActe,
} from '@/types';
import { useActes, useActeIndividus, useMentionAnts } from '@/hooks';

import AssistantFooter from './AssistantFooter';
import AssistantHeader from './AssistantHeader';
import { SelectionStep, VerificationStep } from './Steps';

const Assistant = () => {
  const location = useLocation();

  // Lecture des paramètres URL
  const searchParams = new URLSearchParams(location.search);
  const identifiantActe = searchParams.get('identifiantActe');
  const identifiantActeIndividu = searchParams.get('identifiantActeIndividu');
  const codeMention = searchParams.get('codeMention');
  const typeActeParam = (searchParams.get('typeActe') as TypeActe) ?? undefined;

  // État de sélection — remonté ici pour persister à travers la navigation entre étapes
  const [typeActe, setTypeActe] = useState<TypeActe>(
    typeActeParam ?? 'naissance',
  );
  const [selectedActe, setSelectedActe] = useState<Acte | null>(null);
  const [selectedIndividu, setSelectedIndividu] = useState<ActeIndividu | null>(
    null,
  );
  const [selectedMention, setSelectedMention] = useState<MentionAnts | null>(
    null,
  );

  const actes = useActes(typeActe);
  const acteIndividus = useActeIndividus();
  const mentionAnts = useMentionAnts();

  // identifiantActe et identifiantActeIndividu résolus : param URL ou sélection manuelle
  const [resolvedIdentifiantActe, setResolvedIdentifiantActe] = useState<
    number | null
  >(identifiantActe ? Number(identifiantActe) : null);
  const [resolvedIdentifiantActeIndividu, setResolvedIdentifiantActeIndividu] =
    useState<number | null>(
      identifiantActeIndividu ? Number(identifiantActeIndividu) : null,
    );

  // Si l'identifiant est déjà connu (param URL), on démarre à Verification, sinon à Selection
  const [currentStepIndex, setCurrentStepIndex] = useState<Steps>(
    identifiantActeIndividu ? Steps.Verification : Steps.Selection,
  );

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Mention706702Data | null>(null);

  // Cas URL : récupération automatique des données si les params sont dans l'URL
  useEffect(() => {
    if (
      !resolvedIdentifiantActe ||
      !resolvedIdentifiantActeIndividu ||
      !codeMention
    )
      return;

    setLoading(true);
    request
      .get(
        `/api/administres/etat-civil/comedec/${codeMention}/preparer?typeActe=${typeActe}&identifiantActe=${resolvedIdentifiantActe}&identifiantActeIndividu=${resolvedIdentifiantActeIndividu}`,
      )
      .then((response) => {
        setData(response as Mention706702Data);
      })
      .catch(() => {
        toast.error(
          'Une erreur est survenue lors de la récupération des données',
        );
      })
      .finally(() => setLoading(false));
  }, [
    resolvedIdentifiantActe,
    resolvedIdentifiantActeIndividu,
    codeMention,
    typeActe,
  ]);

  // #region Functions

  const handleStepChange = useCallback(
    (stepIndex: Steps) => {
      if (stepIndex < currentStepIndex) {
        setCurrentStepIndex(stepIndex);
        return;
      }

      // Flux manuel : appel API à la validation de l'étape de sélection
      const isManualSelection =
        currentStepIndex === Steps.Selection &&
        resolvedIdentifiantActe &&
        resolvedIdentifiantActeIndividu &&
        selectedMention;
      if (isManualSelection) {
        setLoading(true);
        request
          .get(
            `/api/administres/etat-civil/comedec/${selectedMention.codificationAnts}/preparer?typeActe=${typeActe}&identifiantActe=${resolvedIdentifiantActe}&identifiantActeIndividu=${resolvedIdentifiantActeIndividu}`,
          )
          .then((response) => {
            setData(response as Mention706702Data);
            setCurrentStepIndex(stepIndex);
          })
          .catch(() => {
            toast.error(
              'Une erreur est survenue lors de la récupération des données',
            );
          })
          .finally(() => setLoading(false));
        return;
      }

      setCurrentStepIndex(stepIndex);
    },
    [
      currentStepIndex,
      resolvedIdentifiantActe,
      resolvedIdentifiantActeIndividu,
      selectedMention,
      typeActe,
    ],
  );

  const handleFinish = useCallback(async () => {
    if (!data) return;

    setLoading(true);
    try {
      await request.post(
        '/api/administres/etat-civil/comedec/mentions/706702',
        data,
      );
      toast.success("L'apposition de la mention a été effectuée avec succès");
    } catch (error) {
      const message =
        (error as { message?: string })?.message ??
        "Une erreur est survenue lors de l'apposition de la mention";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [data]);

  const handleNewSearch = useCallback(() => {
    setSelectedActe(null);
    setSelectedIndividu(null);
    setSelectedMention(null);
    setResolvedIdentifiantActe(null);
    setResolvedIdentifiantActeIndividu(null);
    acteIndividus.reset();
    mentionAnts.reset();
  }, [acteIndividus, mentionAnts]);

  const handleActeSelected = useCallback(
    (acte: Acte) => {
      setSelectedActe(acte);
      setSelectedIndividu(null);
      setSelectedMention(null);
      acteIndividus.get(acte.type, acte.identifiant);
    },
    [acteIndividus],
  );

  const handleIndividuSelected = useCallback(
    (individu: ActeIndividu) => {
      setSelectedIndividu(individu);
      setSelectedMention(null);
      setResolvedIdentifiantActe(selectedActe?.identifiant ?? 0);
      setResolvedIdentifiantActeIndividu(individu.identifiant);
      mentionAnts.get(typeActe);
    },
    [selectedActe, typeActe, mentionAnts],
  );

  const handleMentionSelected = useCallback((mention: MentionAnts) => {
    setSelectedMention(mention);
  }, []);

  const handleTypeActeChange = useCallback(
    (type: TypeActe) => {
      setTypeActe(type);
      setSelectedActe(null);
      setSelectedIndividu(null);
      setSelectedMention(null);
      setResolvedIdentifiantActe(null);
      setResolvedIdentifiantActeIndividu(null);
      acteIndividus.reset();
      mentionAnts.reset();
    },
    [acteIndividus, mentionAnts],
  );

  // #endregion

  const stepToDisplay = useMemo(() => {
    switch (currentStepIndex) {
      case Steps.Selection:
        return (
          <SelectionStep
            typeActe={typeActe}
            onTypeActeChange={handleTypeActeChange}
            selectedActe={selectedActe}
            onActeSelected={handleActeSelected}
            selectedIndividu={selectedIndividu}
            onIndividuSelected={handleIndividuSelected}
            selectedMention={selectedMention}
            onMentionSelected={handleMentionSelected}
            actes={actes}
            acteIndividus={acteIndividus}
            mentionAnts={mentionAnts}
            onNewSearch={handleNewSearch}
          />
        );

      case Steps.Verification:
        return <VerificationStep mentionData={data} loading={loading} />;

      default:
        return <h1>Une erreur est survenue...</h1>;
    }
  }, [
    currentStepIndex,
    typeActe,
    selectedActe,
    selectedIndividu,
    selectedMention,
    actes,
    acteIndividus,
    mentionAnts,
    data,
    loading,
    handleTypeActeChange,
    handleActeSelected,
    handleIndividuSelected,
    handleMentionSelected,
    handleNewSearch,
  ]);

  // Si URL params fournis, masquer l'étape de sélection
  const isUrlMode = !!identifiantActeIndividu;
  const visibleSteps = isUrlMode
    ? _.omit(ASSISTANT_STEPS, Steps.Selection)
    : ASSISTANT_STEPS;

  return (
    <div style={{ height: '100%', padding: '1em' }}>
      <Segment
        raised
        style={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: 'white',
          borderRadius: '16px',
        }}
      >
        {!isUrlMode && (
          <AssistantHeader
            currentStepIndex={currentStepIndex}
            onStepChange={handleStepChange}
            steps={visibleSteps}
          />
        )}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
            padding: '1em',
          }}
        >
          {stepToDisplay}
        </div>
        <AssistantFooter
          currentStepIndex={currentStepIndex}
          onStepChange={handleStepChange}
          onFinish={handleFinish}
          nbSteps={_.size(ASSISTANT_STEPS)}
          loading={loading}
          firstStepIndex={isUrlMode ? Steps.Verification : Steps.Selection}
          nextDisabled={
            currentStepIndex === Steps.Selection &&
            (!resolvedIdentifiantActeIndividu || !selectedMention)
          }
        />
      </Segment>
    </div>
  );
};

export default Assistant;
